import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { isDescriptionEmpty } from '../../pages/EditOpportunity/PublicationForm/PublicationInfoForm';
import { AIAlert } from '../components/AIAlert';
import { ACTION_GENERATE_REJECTION_FEEDBACK } from '../constants/actions';
import { aiPurple } from '../constants/colors';
import {
  SinapseBtnMainProps,
  TextOverwriteWithAIGenerated,
} from '../generate_text/TextOverwriteWithAIGenerated';
import useAsyncSinapseResponse, {
  AsyncSinapseResponseHookProps,
} from '../hooks/useAsyncSinapseResponse';
import { SparkIcon } from '../icons/spark';

const getQueryKeyPath = (sourceId: number) => ['sinapse', 'rejectionFeedback', sourceId];
export const useGenerateRejectionFeedbackStatus = (sourceId?: number) => {
  const { responseStatus: responseTextStatus } = useAsyncSinapseResponse({
    sourceId,
    action: ACTION_GENERATE_REJECTION_FEEDBACK,
    enabled: !!sourceId,
    configs: {
      queryKeyPath: sourceId ? getQueryKeyPath(sourceId) : undefined,
    },
  });
  return responseTextStatus;
};
export const RejectionFeedbackGenerator: React.FC<
  AsyncSinapseResponseHookProps & { triggerEnabled: boolean }
> = ({
  enabled,
  setIsDisabledInput,
  setResult,
  sourceId: recruitmentProcessId,
  textValue = '',
  input = '',
  triggerEnabled = false,
}) => {
  const { discartResponseResult, processResult, responseStatus } = useAsyncSinapseResponse({
    setIsDisabledInput,
    setResult,
    sourceId: recruitmentProcessId,
    textValue,
    input,
    action: ACTION_GENERATE_REJECTION_FEEDBACK,
    enabled,
    configs: {
      overwriteOnSuccess: true,
      queryKeyPath: recruitmentProcessId ? getQueryKeyPath(recruitmentProcessId) : undefined,
    },
  });

  if (!enabled) {
    return null;
  }

  return (
    <>
      <AIAlert
        key="ai-alert-rejection-feedback"
        showIcon
        icon={<SparkIcon style={{ fontSize: '1.5em', color: aiPurple }} />}
        action={
          <GenerateRejectionFeedbackButton
            discartResponseText={
              // confirm fn
              discartResponseResult
            }
            processText={processResult} // initiator
            responseTextStatus={responseStatus} // state
            textValue={textValue}
            isTextEmpty={isDescriptionEmpty}
            btnProps={{
              disabled: !triggerEnabled,
            }}
          />
        }
        message={
          <>
            <b>
              <Trans>Feedback personalizado</Trans>:{' '}
            </b>
            <Trans>
              Ofereça uma experiência diferenciada para quem participa do seu processo seletivo
              utilizando a Synapses, Inteligência Artificial da Mindsight, para gerar
              automaticamente uma devolutiva personalizada.
            </Trans>
          </>
        }
      />
    </>
  );
};
export const GenerateRejectionFeedbackButton: React.FC<SinapseBtnMainProps> = (props) => {
  const { t } = useTranslation();

  const { disabled } = props.btnProps || {};
  const modalProps = {
    title: t('Sobrescrever o email atual?'),
    content: t(
      'Ao gerar o feedback personalizado, o email atual será substituído pela sugestão da Synapses. Você tem certeza que deseja prosseguir?'
    ),
    okText: t('Sim'),
    cancelText: t('Não'),
  };
  return (
    <TextOverwriteWithAIGenerated
      confirmOverwrite
      {...props}
      confirmProps={modalProps}
      btnContent={
        <Tooltip
          open={disabled}
          autoAdjustOverflow
          getPopupContainer={(triggerNode: HTMLElement) => triggerNode.parentElement as HTMLElement}
          title={
            <>
              <ExclamationCircleOutlined style={{ color: '#faad14', marginLeft: '5px' }} />{' '}
              <Trans>Preencha a justificativa para gerar o feedback personalizado</Trans>
            </>
          }
          placement="bottom"
          overlayInnerStyle={{
            maxWidth: 250,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            textAlign: 'center',
          }}
        >
          {' '}
          <Trans>Gerar feedback</Trans>
        </Tooltip>
      }
    />
  );
};
